import isNil from "lodash/isNil";
import { taskActions } from "../../../redux/task/task.slice";
import omitBy from "lodash/omitBy";
import { appActions } from "../../../redux/app/app.slice";
import { entryActions } from "../../../redux/entry/entry.slice";
import isString from "lodash/isString";
import { AppDispatch } from "../../../redux/store";
import { Entry } from "../../../model/entry/Entry";
import { UseFormGetValues } from "react-hook-form";
import { GetTasksFilterDto } from "../../../model/task/dto/GetTasksFilterDto";
import { BaseTask } from "../../../model/task/Task";
import { KanbanCard } from "../../../model/kanban/KanbanCard";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { AtiraToast } from "../../../utils/AtiraToast";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { TaskKind } from "../../../model/task/shared/TasksKind.enum";

type Props = {
  userId: string;
  entryId: string;
  taskId?: string;
  task?: BaseTask | null;
  kanbanCardId?: string;
  pathname: string;
  tasksPage: number;
  tasksPageSize: number;
  dispatch: AppDispatch;
  setEntry: ATVoidFunction<Entry>;
  setTask: ATVoidFunction<BaseTask>;
  setKanbanCard: ATVoidFunction<KanbanCard>;
  contextGetValues: UseFormGetValues<GetTasksFilterDto>;
  isFloatingTodayTasksOpen?: boolean;
  searchKeyword?: string;
};

export default async function fetchAfterTaskAction({
  userId,
  entryId,
  taskId,
  task,
  kanbanCardId,
  pathname,
  tasksPage,
  tasksPageSize,
  dispatch,
  setEntry,
  setTask,
  setKanbanCard,
  contextGetValues,
  isFloatingTodayTasksOpen,
  searchKeyword,
}: Props) {
  const fetchOnTasksAndDealsPage = async () => {
    try {
      const [{ data }, kanbanCard] = await Promise.all([
        dispatch(
          taskActions.getAllTasks({
            userId,
            meta: {
              page: tasksPage - 1,
              count: tasksPageSize,
            },
            ...omitBy(
              {
                keyword: contextGetValues("keyword"),
                kind: contextGetValues("kind"),
                reminder: contextGetValues("reminder"),
                done: contextGetValues("done"),
                range: contextGetValues("range"),
                scope: contextGetValues("scope"),
              },
              isNil,
            ),
          }),
        ).unwrap(),
        kanbanCardId &&
        task?.parentId === kanbanCardId &&
        task?.kind === TaskKind.KANBAN
          ? dispatch(
              kanbanActions.getKanbanCardById({ userId, cardId: kanbanCardId }),
            ).unwrap()
          : undefined,
      ]);

      const updatedTask = data?.find((t) => t._id === taskId);

      if (updatedTask) {
        setTask(updatedTask);

        if ((updatedTask?.parent as Entry)?.kind) {
          setEntry(updatedTask.parent as Entry);
        } else if (kanbanCard) {
          setKanbanCard(kanbanCard);
        } else {
          setKanbanCard(updatedTask.parent as KanbanCard);
        }
      }
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const fetchOnSearchPage = async () => {
    try {
      const { results } = await dispatch(
        appActions.search({
          userId,
          keyword: searchKeyword!,
        }),
      ).unwrap();

      const updatedTask = results?.find(
        (t) => t._kind === "task" && t._id === taskId,
      );
      const updatedEntry = results?.find(
        (t) => t._kind === "entry" && t._id === entryId,
      );

      if (updatedTask) {
        setTask(updatedTask as BaseTask);
      }

      if (updatedEntry) {
        setEntry(updatedEntry as Entry);
      }
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const fetchOnEntriesPage = async () => {
    try {
      const entry = await dispatch(
        entryActions.getEntryById({ userId, entryId }),
      ).unwrap();
      setEntry(entry);
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  const onFetchTodayTasks = async () => {
    try {
      const { data } = await dispatch(
        taskActions.getTodayTasks({
          userId,
          meta: PageMeta.create({ page: 0, count: 25 }),
        }),
      ).unwrap();

      const updatedTask = data?.find((t) => t._id === taskId);

      if (updatedTask) {
        setTask(updatedTask);
      }
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    }
  };

  if (isFloatingTodayTasksOpen) {
    return await onFetchTodayTasks();
  }

  if (pathname.includes("/entries")) {
    return await fetchOnEntriesPage();
  }

  if (pathname.includes("/tasks") || pathname.includes("/deals")) {
    return await fetchOnTasksAndDealsPage();
  }

  if (pathname.includes("/search") && isString(searchKeyword)) {
    return await fetchOnSearchPage();
  }
}
